/**
 * Managers API Service (KAN-744): справочник менеджеров и закрепление.
 *
 *   GET    /api/v1/managers/?active=true|false|all  - {results: [manager]}
 *   POST   /api/v1/managers/                        - {last_name, first_name, middle_name?, phone?, notes?}
 *   GET    /api/v1/managers/{id}/                   - manager + summary
 *   PATCH  /api/v1/managers/{id}/                   - любые поля, is_active (архив)
 *   DELETE /api/v1/managers/{id}/                   - 204 или 409 {code: "manager_in_use", detail}
 *   GET    /api/v1/managers/{id}/funnel/?period=&channel= - формат /analytics/funnel/stages/
 *   POST   /api/v1/managers/{id}/account/          - KAN-811: создать вход {email, password}
 *   PATCH  /api/v1/managers/{id}/account/          - KAN-811: сменить пароль {password}
 *   POST   /api/v1/managers/{id}/account/enable/   - KAN-811: включить вход (без тела)
 *   POST   /api/v1/managers/{id}/account/disable/  - KAN-811: отключить вход (без тела)
 *   Ответ методов входа: {email, is_active}.
 *   GET    /api/v1/managers/me/                    - KAN-837: свой профиль менеджера
 *   PATCH  /api/v1/managers/me/                    - KAN-837: ФИО, телефон, work_hours; 400 {field: [msg]}
 *   POST   /api/v1/managers/me/credentials/        - KAN-846: {email?, password, password_confirm} свой
 *                                                    логин и пароль (пока вход без пароля); ответ как GET me/
 *
 * Закрепление менеджера за клиентом и заказом тоже здесь: адрес меняется в
 * одном месте, если backend вынесет его в отдельный endpoint.
 */
import apiClient from './client';
import type { Client } from '@/types/client';
import type { EstimateDetail } from '@/types/estimate';
import type { FunnelStageStatsResponse, FunnelStatsChannel, FunnelStatsPeriod } from '@/types/funnel';
import type {
  Manager,
  ManagerAccount,
  ManagerAccountCreateBody,
  ManagerAccountUpdateBody,
  ManagerActiveFilter,
  ManagerBody,
  ManagerCredentialsBody,
  ManagerDetail,
  ManagerSelfBody,
  ManagerSelfProfile,
} from '@/types/manager';

const BASE = '/api/v1/managers/';
const ME = `${BASE}me/`;

export const managersApi = {
  list: async (active: ManagerActiveFilter = 'true'): Promise<Manager[]> => {
    const response = await apiClient.get(BASE, { params: { active } });
    const d = response.data;
    return Array.isArray(d) ? d : (d?.results ?? []);
  },
  get: async (id: string): Promise<ManagerDetail> =>
    (await apiClient.get<ManagerDetail>(`${BASE}${id}/`)).data,
  create: async (data: ManagerBody): Promise<Manager> =>
    (await apiClient.post<Manager>(BASE, data)).data,
  update: async (id: string, data: ManagerBody): Promise<Manager> =>
    (await apiClient.patch<Manager>(`${BASE}${id}/`, data)).data,
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE}${id}/`);
  },
  getFunnel: async (
    id: string,
    period: FunnelStatsPeriod,
    channel: FunnelStatsChannel
  ): Promise<FunnelStageStatsResponse> =>
    (
      await apiClient.get<FunnelStageStatsResponse>(`${BASE}${id}/funnel/`, {
        params: { period, channel },
      })
    ).data,
  /** KAN-811: создать вход менеджера в кабинет. */
  createAccount: async (id: string, data: ManagerAccountCreateBody): Promise<ManagerAccount> =>
    (await apiClient.post<ManagerAccount>(`${BASE}${id}/account/`, data)).data,
  /** KAN-811: сменить пароль входа. */
  updateAccount: async (id: string, data: ManagerAccountUpdateBody): Promise<ManagerAccount> =>
    (await apiClient.patch<ManagerAccount>(`${BASE}${id}/account/`, data)).data,
  /** KAN-811: включить (true) или отключить (false) вход. */
  setAccountEnabled: async (id: string, enabled: boolean): Promise<ManagerAccount> =>
    (await apiClient.post<ManagerAccount>(`${BASE}${id}/account/${enabled ? 'enable' : 'disable'}/`)).data,
  /** KAN-837: свой профиль вошедшего менеджера. */
  getMe: async (): Promise<ManagerSelfProfile> => (await apiClient.get<ManagerSelfProfile>(ME)).data,
  /** KAN-837: сохранить свой профиль (любые поля). */
  updateMe: async (data: ManagerSelfBody): Promise<ManagerSelfProfile> =>
    (await apiClient.patch<ManagerSelfProfile>(ME, data)).data,
  /** KAN-846: задать свой логин и пароль (вход по ссылке без пароля). */
  setMyCredentials: async (data: ManagerCredentialsBody): Promise<ManagerSelfProfile> =>
    (await apiClient.post<ManagerSelfProfile>(`${ME}credentials/`, data)).data,
  /** Менеджер клиента; null: снять. */
  assignToClient: async (clientId: string, managerId: string | null): Promise<Client> =>
    (await apiClient.patch<Client>(`/api/v1/clients/${clientId}/`, { manager_id: managerId })).data,
  /** Менеджер заказа; null: снять. */
  assignToEstimate: async (
    estimateId: string,
    managerId: string | null
  ): Promise<EstimateDetail> =>
    (
      await apiClient.patch<EstimateDetail>(`/api/v1/estimates/${estimateId}/`, {
        manager_id: managerId,
      })
    ).data,
};
